import { defineStore } from "pinia"

import * as configurationApi from "@/api/configurationApi.js"

import communityDefaultLogoSrc from "@/assets/community-default-logo.jpg"

import routes from "@/routes.js"

import * as sentryReporter from "@/services/sentryReporter.js"

import { defaultColors } from "@/constants/defaults.js"

const defaultPasswordLength = 8
const defaultSupportEmail = "support@disciplemedia.com"

export const useConfigurationStore = defineStore("configuration", {
  state: () => ({
    configurationState: {
      analytics: {},
      isPremium: false,
      webRegistrationEnabled: true,
      inviteOnly: false,
      useMagicLinks: false,
      appTitle: null,
      artistLongName: null,
      registrationPageTitle: null,
      registrationPageIntroduction: null,
      appStoreUrl: null,
      googlePlayUrl: null,
      appIconUrl: null,
      privacyPolicyVersion: null,
      termsAndConditionsVersion: null,
      pubnub: null,
      postSearchEnabled: false,
      folderSearchEnabled: false,
      whiteLabel: false,
      urlIcons: {},
      emailConfirmationEnabled: false,
      emailConfirmationCaptcha: {
        enabled: false,
        sitekey: null
      },
      notifications: null,
      friendsMessagingEnabled: false,
      livestreamPremiumRequired: false,
      livestreamPremiumPlanRestrictions: [],
      livestreamMultipleStreamers: false,
      subscriptionNudges: {},
      stripeKey: null,
      stripeConnectAccountId: null,
      navigation: [],
      homePageRoute: { name: routes.GROUPS },
      homePageSections: {},
      invitationLinkOnActivityFeed: false,
      invitationLinkOnMoreMenu: false,
      colors: defaultColors,
      passwordMinLength: defaultPasswordLength,
      supportEmail: defaultSupportEmail,
      emailNotificationsEnabled: false,
      newMembersInActivityFeed: false,
      memberDirectoryEnabled: false,
      rtmpEnabled: false,
      agoraAppId: null,
      maxHosts: null,
      paywallLockEnabled: false,
      availableLanguages: [],
      gifUploadEnabled: true,
      walkthrough: null,
      usernameCustomisations: null,
      cookiesStrictMode: false,
      saml: {
        enabled: false
      },
      openid: {
        enabled: false
      },
      pwaRef: null
    },
    limitsState: {
      livestreams: { reached: false },
      videos: { reached: false }
    },
    profileFieldHeaderLogoEnabled: false,
    onboardingVideoUrl: null,
    onboardingImage: null
  }),
  getters: {
    configuration: (state) => state.configurationState,
    limits: (state) => state.limitsState
  },
  actions: {
    async fetchLimits() {
      try {
        const limitsData = await configurationApi.getLimits()
        this.limitsState = limitsData
      } catch (err) {
        sentryReporter.reportError(err)
      }
    },
    async fetchConfiguration({ includeRegistrationSnippets } = {}) {
      const configFetchPromises = [configurationApi.getConfiguration()]

      if (includeRegistrationSnippets) {
        configFetchPromises.push(configurationApi.getRegistrationConfiguration())
      }

      const [configurationDataPromise, registrationConfigurationDataPromise] = await Promise.allSettled(configFetchPromises)

      const configurationData = configurationDataPromise?.value
      const registrationConfigurationData = registrationConfigurationDataPromise?.value

      let mutationAttributes = {
        ...this.configuration
      }

      ;[configurationDataPromise, registrationConfigurationDataPromise].forEach((promise) => {
        if (promise?.status === "rejected") {
          sentryReporter.reportError(promise.reason)
        }
      })

      if ([configurationDataPromise, registrationConfigurationDataPromise].every((p) => !p || p.status === "rejected")) {
        return
      }

      if (configurationData) {
        const appIcons = configurationData.app_icons || {}
        const logoSrc = appIcons["120x120"] || appIcons[Object.keys(appIcons)[0]] || communityDefaultLogoSrc

        mutationAttributes = {
          analytics: configurationData.analytics,
          isPremium: configurationData.subscriptions_enabled,
          webRegistrationEnabled: configurationData.web_registration_enabled && !configurationData.login_using_magic_links,
          inviteOnly: configurationData.registration_invitation_only,
          useMagicLinks: configurationData.login_using_magic_links,
          appTitle: configurationData.app_name,
          artistLongName: configurationData.artist_long_name,
          registrationPageTitle: this.configuration.registrationPageTitle,
          registrationPageIntroduction: this.configuration.registrationPageIntroduction,
          appStoreUrl: configurationData.store_urls?.app_store,
          googlePlayUrl: configurationData.store_urls?.play_store,
          appIconUrl: logoSrc,
          privacyPolicyVersion: configurationData.legal?.privacy_policy.version,
          termsAndConditionsVersion: configurationData.legal?.terms_conditions.version,
          colors: configurationData.colors || defaultColors,
          pubnub: configurationData.pubnub,
          postSearchEnabled: configurationData.post_search,
          folderSearchEnabled: configurationData.folder_search_enabled,
          notifications: configurationData.notifications,
          whiteLabel: configurationData.white_label,
          urlIcons: configurationData.url_icons,
          emailConfirmationEnabled: configurationData.email_confirmation_enabled,
          emailConfirmationCaptcha: {
            enabled: Boolean(configurationData.email_confirmation_captcha?.enabled),
            sitekey: configurationData.email_confirmation_captcha?.sitekey
          },
          friendsMessagingEnabled: configurationData.friends_messaging_enabled,
          subscriptionNudges: configurationData.subscription_nudges || {},
          stripeKey: configurationData.stripe_publishable_key,
          stripeConnectAccountId: configurationData.stripe_connect_account_id,
          navigation: configurationData.navigation || [],
          homePageRoute: getHomePage(configurationData.home_navigation_item),
          homePageSections: configurationData.home_screen_navigation?.sections || {},
          livestreamPremiumRequired: configurationData.livestream_premium_required,
          livestreamPremiumPlanRestrictions: configurationData.livestream?.subscription_plan_restrictions || [],
          livestreamMultipleStreamers: configurationData.multiple_streamers_enabled,
          invitationLinkOnActivityFeed: configurationData.invitation_link_activity_feed_enabled,
          invitationLinkOnMoreMenu: configurationData.invitation_link_more_menu_enabled,
          passwordMinLength: configurationData.password_validations?.min_length || defaultPasswordLength,
          supportEmail: configurationData.support_email || defaultSupportEmail,
          emailNotificationsEnabled: configurationData.email_notifications_enabled,
          newMembersInActivityFeed: configurationData.new_members_in_activity_feed,
          memberDirectoryEnabled: configurationData.member_directory_enabled,
          rtmpEnabled: configurationData.livestream_rtmp_enabled || configurationData.cloudflare_backed_rtmp,
          agoraAppId: configurationData.livestream?.agora_app_id,
          maxHosts: configurationData.livestream?.max_hosts || 1,
          paywallLockEnabled: configurationData.paywall_lock_enabled,
          availableLanguages: configurationData.available_languages?.split(",") || [],
          gifUploadEnabled: configurationData.gif_upload_enabled,
          walkthrough: configurationData.walkthrough || { enabled: false },
          usernameCustomisations: configurationData.username_customisations,
          openid: configurationData.openid_connect || { enabled: false },
          saml: configurationData.saml || { enabled: false },
          profileFieldHeaderLogoEnabled: configurationData.profile_field_header_logo_enabled,
          onboardingVideoUrl: configurationData.onboarding_video_url,
          onboardingImage: configurationData.launch_screen_image,
          cookiesStrictMode: configurationData.pwa_cookie_strict_mode,
          pwaRef: configurationData.pwa_ref
        }
      }

      if (registrationConfigurationData) {
        mutationAttributes.registrationPageTitle = registrationConfigurationData.title
        mutationAttributes.registrationPageIntroduction = registrationConfigurationData.intro
      }

      this.configurationState = mutationAttributes
    }
  }
})

const getHomePage = (homeNavigationItem) => {
  switch (homeNavigationItem?.type) {
    case "wall":
      return { name: routes.GROUPS, params: { groupId: homeNavigationItem.wall_id } }
    case "archive":
      return { name: routes.FOLDER, params: { folderId: homeNavigationItem.folder_id } }
    default:
      return { name: routes.GROUPS }
  }
}
